import { Component, Inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { api } from '@src/app/app.const';
import { AppApiService } from '@src/app/core/services/app-api.service';
import { ContactByTypes } from '@src/app/mepco-public/models/contact-by.model';
import { VerificationStatus } from '@src/app/mepco-public/models/verification-status.model';
import { PasswordStrengthModel } from '@src/app/shared/components/password-strength/interfaces/password-strength.model';
import { RequestResponse } from '../forgot-username/forgot-username-dialog.component';

export interface PasswordPolicies {
  minimumLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumbers?: boolean;
  requireSymbols?: boolean;
}
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password-dialog.component.html',
  styleUrls: ['./reset-password-dialog.component.css'],
  standalone: false,
})
export class ResetPasswordDialogComponent {
  userFormGroup: FormGroup;
  verificationFormGroup: FormGroup;
  passwordFormGroup: FormGroup;
  isLoading = false;

  passwordPolicies: PasswordPolicies;

  passwordStrengthForm = signal<PasswordStrengthModel>({
    confirmPassword: '',
    password: '',
    valid: false,
  });

  pending = VerificationStatus.Pending;
  maxAttemptsReached = VerificationStatus.MaxAttemptsReached;
  maxCheckAttemptsReached = VerificationStatus.MaxCheckAttemptsReached;
  error = VerificationStatus.Error;
  failed = VerificationStatus.Failed;
  approved = VerificationStatus.Approved;

  emailType = ContactByTypes.EMAIL;
  phoneType = ContactByTypes.PHONE;
  contactSelected = '';

  verificationCodeResponse = signal<RequestResponse | null>(null);
  changePasswordResponse = signal<RequestResponse | null>(null);
  attempts: any;

  constructor(
    private apiService: AppApiService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ResetPasswordDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private _data: any
  ) {
    this.userFormGroup = this.fb.group({
      username: ['', Validators.required],
      contactMethod: [this.phoneType, Validators.required],
      email: ['', [Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]],
      phone: ['', [Validators.maxLength(30)]],
    });

    this.setContactValidators(this.phoneType);

    this.createPasswordForm();
  }

  private _changePasswordForm: FormGroup;

  get changePasswordForm(): FormGroup {
    return this._changePasswordForm;
  }

  getFormValues(event: PasswordStrengthModel) {
    this.passwordStrengthForm.set(event);
  }

  onContactMethodChange(contactMethod: string): void {
    this.userFormGroup.patchValue({
      email: '',
      phone: '',
    });
    this.userFormGroup.markAsUntouched();

    this.setContactValidators(contactMethod);
    this.resetResponses();
  }

  setContactValidators(contactMethod: string) {
    const emailControl = this.userFormGroup.get(this.emailType);
    const phoneControl = this.userFormGroup.get(this.phoneType);

    if (contactMethod === this.emailType) {
      phoneControl?.clearValidators();
      emailControl?.setValidators([Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]);
      this.contactSelected = this.emailType;
    } else {
      emailControl?.clearValidators();
      phoneControl?.setValidators([Validators.required, Validators.maxLength(30)]);
      this.contactSelected = this.phoneType;
    }

    emailControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
  }

  async onSendVerificationCode(stepper: MatStepper): Promise<void> {
    if (this.userFormGroup.invalid) return;

    this.isLoading = true;
    const body = {
      ...this.getFormData(),
      username: this.userFormGroup.controls.username?.value,
    };

    try {
      const response = await this.apiService.postWithoutToken(
        api.webview.name,
        `user/forgotPassword/sendVerificationCode`,
        { body },
        false
      );
      if (response.status === this.pending) {
        this.attempts = response.attempts;
        const passwordPolicies = await this.apiService.getWithoutToken(api.webview.name, `user/passwordPolicies`, null);

        if (passwordPolicies) {
          this.passwordPolicies = passwordPolicies;
          this.setValidators();
        }

        this.verificationCodeResponse.set({
          ...response,
          detail: 'The verification code has been sent successfully.',
        });

        this.isLoading = false;

        setTimeout(() => {
          this.resetResponses();
          stepper.next();
        }, 1500);
      } else if (response.status === this.maxAttemptsReached) {
        this.verificationCodeResponse.set({
          ...response,
          detail: 'Maximum send attempts reached. Please try again later.',
        });
      } else {
        const contactMethod = this.userFormGroup.controls.contactMethod?.value;

        if (contactMethod === this.emailType) {
          const emailControl = this.userFormGroup.controls.email;
          emailControl.setErrors({ notFound: true });
        }
        if (contactMethod === this.phoneType) {
          const phoneControl = this.userFormGroup.controls.phone;
          phoneControl.setErrors({ notFound: true });
        }
      }
      this.isLoading = false;
    } catch (error) {
      const errorData: RequestResponse = {
        status: this.error,
        attempts: error?.attempts || 0,
        detail:
          error.response?.detail ||
          'An error occurred. Please wait a moment and try again. If the issue persists, contact the administrator for assistance.',
        title: error.response?.title || 'Error',
      };
      this.verificationCodeResponse.set(errorData);
      this.isLoading = false;
    }
  }

  private getFormData(): { phone?: string; email?: string } {
    const contactMethod = this.userFormGroup.controls.contactMethod?.value;

    if (contactMethod === this.phoneType) {
      return { phone: this.userFormGroup.controls.phone?.value };
    } else if (contactMethod === this.emailType) {
      return { email: this.userFormGroup.controls.email?.value };
    }
    return {};
  }

  createPasswordForm() {
    this._changePasswordForm = new FormGroup({
      verificationCode: new FormControl(''),
    });
  }

  setValidators(): void {
    const verificationCode = [Validators.required];

    this.changePasswordForm.controls.verificationCode.setValidators(verificationCode);
    this.changePasswordForm.controls.verificationCode.updateValueAndValidity();
  }

  async onSendVerifyCode(stepper: MatStepper): Promise<void> {
    this.resetResponses();

    if (this.changePasswordForm.invalid) return;

    const body = {
      ...this.getFormData(),
      verificationCode: this.changePasswordForm.controls.verificationCode.value,
      username: this.userFormGroup.controls.username.value,
      password: this.passwordStrengthForm().password,
    };

    this.isLoading = true;

    try {
      const response = await this.apiService.postWithoutToken(
        api.webview.name,
        `user/forgotPassword/resetPassword`,
        { body: body },
        false
      );
      if (response.status === this.approved) {
        this.changePasswordResponse.set({
          ...response,
          detail: 'Your password has been successfully changed.',
        });
        this.isLoading = false;
        setTimeout(() => {
          stepper.next();
        }, 1500);
      } else if (response.status === this.maxCheckAttemptsReached) {
        this.changePasswordResponse.set({
          ...response,
          detail: 'Maximum check attempts reached. Please try again later.',
        });
      } else {
        const verificationCodeControl = this.changePasswordForm.controls.verificationCode;
        verificationCodeControl.setErrors({ notFound: true });
      }
      this.isLoading = false;
    } catch (error) {
      this.handleError(error.response);
    }
  }

  private resetResponses(): void {
    this.verificationCodeResponse.set(null);
    this.changePasswordResponse.set(null);
  }

  onResendCode(): void {
    this.isLoading = true;
    this.resetResponses();

    this.sendVerificationCodeRequest()
      .then(data => {
        if (data.status === this.pending) {
          this.attempts = data.attempts;
          data.detail = 'The verification code has been sent successfully.';
          this.verificationCodeResponse.set(data);
        }
      })
      .catch(error => {
        this.handleError(error.response);
      })
      .finally(() => (this.isLoading = false));
  }

  private sendVerificationCodeRequest(): Promise<RequestResponse> {
    const body = {
      ...this.getFormData(),
      username: this.userFormGroup.controls.username?.value,
    };

    return this.apiService.postWithoutToken(
      api.webview.name,
      `user/forgotPassword/sendVerificationCode`,
      { body: body },
      false
    );
  }

  handleError(error?: any) {
    const errorData: RequestResponse = {
      status: error?.status || this.error,
      attempts: error?.attempts || 0,
      detail:
        error?.errorMessage ||
        'An error occurred. Please wait a moment and try again. If the issue persists, contact the administrator for assistance',
      title: error?.title || 'Error',
    };
    this.changePasswordResponse.set(errorData);
  }
}
